/**
 * Owns all client-side state for an in-flight server-side ("remote")
 * agent run.
 *
 * "Remote" means a run we did not initiate from the local Composer:
 * typically a replay re-attached after a refresh or in another tab.
 * Local POST runs leave `isRunningRemotely` false.
 *
 * Responsibilities:
 *  - On mount, if `/connect` reported the conversation as in-flight,
 *    arm the agent for replay and trigger the runtime's resume flow.
 *  - Re-attach the same way when a live stream dies mid-run.
 *  - Track lifecycle events (RUN_STARTED / RUN_FINISHED / RUN_ERROR)
 *    to drive `isRunningRemotely` and the cross-conversation
 *    `running-conversations` registry.
 *  - Reload persisted history once a replay run settles, so the thread
 *    reflects the canonical post-run state.
 *  - Expose the cancel button handler.
 */
import { useCallback, useEffect, useState } from "react";
import type { AssistantRuntime, ThreadMessageLike } from "@assistant-ui/react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import { ConversationStatus } from "~/components/assistant/types";
import type { ReplayHttpAgent } from "./ReplayHttpAgent";
import { markConversationIdle, markConversationRunning } from "./running-conversations";

// Ceiling on consecutive self-heal attempts. Each one only fires when the
// server still reports the run alive, so the realistic case is a single
// re-attach per drop; this exists so a stream that dies the instant it opens
// can't spin status-probe/re-attach forever. Reset when a run starts.
const MAX_REATTACH_ATTEMPTS = 3;

interface UseRemoteRunOptions {
  agent: ReplayHttpAgent;
  runtime: AssistantRuntime;
  api: AssistantApi;
  conversationId: string;
  /** Conversation status from /connect; drives replay attach on mount. */
  status: ConversationStatus;
  /**
   * Pre-converted /connect messages (incl. the in-flight user prompt).
   * Used to anchor `resumeRun`'s parentId on the seeded prompt's id —
   * `runtime.thread.getState()` is stale immediately after `reset()`
   * because the external-store core syncs through a re-render.
   */
  initialMessages?: ThreadMessageLike[] | null;
  clearError?: () => void;
  /** Resync the thread with persisted state after replay/cancel settles. */
  onHistoryReload: () => Promise<void>;
}

interface UseRemoteRunReturn {
  isRunningRemotely: boolean;
  isCancelling: boolean;
  onCancelRun: () => void;
}

export function useRemoteRun({
  agent,
  runtime,
  api,
  conversationId,
  status,
  initialMessages,
  clearError,
  onHistoryReload,
}: UseRemoteRunOptions): UseRemoteRunReturn {
  const [isRunningRemotely, setIsRunningRemotely] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [reattachAttempts, setReattachAttempts] = useState(0);

  // A stream killed at the transport layer — a load balancer idle timeout, a
  // VPN appliance closing the connection — reports no AG-UI event to say the
  // turn died, and the run itself keeps going server-side. Ask whether it is
  // still alive; if so, re-attach to its replay so the thread keeps rendering
  // instead of stranding a half-written turn behind an error the user can only
  // clear by refreshing.
  //
  // Aborts never reach here: `ReplayHttpAgent.onError` drops errors once its
  // own signal has fired, which covers both cancel and unmount.
  const reattachAfterDrop = useCallback(async () => {
    if (isCancelling || reattachAttempts >= MAX_REATTACH_ATTEMPTS) {
      return;
    }
    let isRunning: boolean;
    try {
      ({ is_running: isRunning } = await api.getRunStatus(conversationId));
    } catch (error) {
      // Best-effort: the drop already surfaced an error banner, and a failed
      // probe means we can't tell a live run from a finished one. Leave the
      // banner up rather than guess.
      console.error("Failed to probe run status after a dropped stream:", error);
      return;
    }
    if (!isRunning) {
      return;
    }

    // `resumeRun` resets the thread head to `parentId`, so anchor on the turn's
    // user prompt: the replay re-sends the whole run from its first event, and
    // the partial assistant message it already painted has to give way to it.
    const lastUserMessageId = runtime.thread
      .getState()
      .messages.findLast((message) => message.role === "user")?.id;
    if (!lastUserMessageId) {
      return;
    }

    setReattachAttempts((attempts) => attempts + 1);
    clearError?.();
    agent.enableReplayForNextRun();
    runtime.thread.resumeRun({ parentId: lastUserMessageId });
  }, [agent, api, clearError, conversationId, isCancelling, reattachAttempts, runtime]);

  useEffect(() => {
    const sub = agent.subscribe({
      onRunStartedEvent: () => setReattachAttempts(0),
      onRunFailed: () => void reattachAfterDrop(),
    });
    return () => sub.unsubscribe();
  }, [agent, reattachAfterDrop]);

  useEffect(() => {
    const cid = conversationId;
    const markRunFinished = () => {
      setIsRunningRemotely(false);
      markConversationIdle(cid);
    };
    const sub = agent.subscribe({
      onRunStartedEvent: () => {
        setIsRunningRemotely(agent.isReplaying);
        markConversationRunning(cid);
      },
      onRunFinishedEvent: markRunFinished,
      onRunFailed: markRunFinished,
      onRunFinalized: () => {
        // Replay-only: pull persisted history so the thread reflects the
        // canonical post-run state. Local runs already have it in memory.
        if (agent.isReplaying) {
          void onHistoryReload();
        }
      },
    });

    if (status === ConversationStatus.MESSAGE_IN_PROGRESS) {
      // `resumeRun` calls `startRun(parentId)` → `resetHead(parentId)`,
      // which wipes all messages when parentId is null. Anchor on the
      // in-flight user prompt's id from /connect so the assistant
      // placeholder is appended after it instead of erasing it.
      // `runtime.thread.getState()` is unreliable here — `reset()` ran
      // synchronously above but the external-store repo only catches up
      // on the next render.
      const parentId = initialMessages?.at(-1)?.id ?? null;
      agent.enableReplayForNextRun();
      runtime.thread.resumeRun({ parentId });
    }

    return () => {
      sub.unsubscribe();
      agent.abortRun();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  const onCancelRun = useCallback(() => {
    clearError?.();
    agent.abortRun();
    setIsCancelling(true);

    api.cancelMessage(conversationId).then(
      async () => {
        setIsCancelling(false);
        setIsRunningRemotely(false);
        markConversationIdle(conversationId);
        await onHistoryReload();
      },
      (error) => {
        console.error("Failed to cancel run:", error);
        setIsCancelling(false);
        setIsRunningRemotely(false);
        markConversationIdle(conversationId);
      }
    );
  }, [api, agent, conversationId, clearError, onHistoryReload]);

  return { isRunningRemotely, isCancelling, onCancelRun };
}
